/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import { IDictionary } from '@msdyn365-commerce/core';
import * as React from 'react';
import { ICartlinesViewProps } from '../../../modules/smwe-cart/components/smwe-cart-line-items';
import { ICartViewProps } from '../../../modules/smwe-cart/smwe-cart';

const _renderCartlines = (
  cartLines: ICartlinesViewProps[] | undefined,
  cartEmptyText: string,
  CartlinesWrapper: INodeProps,
  storeSelector: React.ReactNode | undefined,
  backToShoppingButton: React.ReactNode,
  waitingComponent: React.ReactNode,
  cartLoadingStatus: string,
  cartDataResult: boolean,
  itemsTitle: string,
  qtyTitle: string,
  unitPriceTitle: string,
  totalTitle: string
): JSX.Element[] | JSX.Element => {
  if (cartLoadingStatus) {
    return <>{cartLoadingStatus}</>;
  }
  if (cartLines) {
    return (
      <div>
        <div className='cart-lineitems-header'>
          <div className='header-items'>{itemsTitle}</div>
          <div className='header-qty'>{qtyTitle}</div>
          <div className='header-unitprice'>{unitPriceTitle}</div>
          <div className='header-total'>{totalTitle}</div>
        </div>

        <ul className='cart-lineitems-container'>
          {cartLines.map((cartLine, index) => {
            return (
              <li className='msc-cart-lines-item' key={index}>
                {cartLine.cartline}
                {storeSelector && cartLine.pickUpInStore ? (
                  <Node {...cartLine.pickUpInStore.ContainerProps}>
                    {cartLine.pickUpInStore.defaultComponent}
                  </Node>
                ) : null}
                {cartLine.addToWishlist}
              </li>
            );
          })}
        </ul>
      </div>
    );
  } else {
    return cartDataResult ? (
      <div className='msc-cart__empty-cart'>
        <p className='msc-cart-line'>{cartEmptyText}</p>
        {backToShoppingButton}
      </div>
    ) : (
      <>{waitingComponent}</>
    );
  }
};

const _renderOrderSummarylines = (
  orderSummaryLines: IOrderSummaryLines | undefined,
  OrderSummaryItems: INodeProps,
  props: ICartViewProps
): JSX.Element | null => {
  if (!orderSummaryLines) {
    return null;
  }
  return (
    <Node {...OrderSummaryItems}>
      {orderSummaryLines.subtotal}
      {orderSummaryLines.shipping}
      {orderSummaryLines.tax}
      {orderSummaryLines.totalDiscounts
        ? orderSummaryLines.totalDiscounts
        : null}
      {orderSummaryLines.orderTotal}
      <p className='cart-order-summary-asterix'>{props.resources.conditionsText}</p>
    </Node>
  );
};

// Detect invalid items in the cart. Items are flagged externally via the Comment field
const _isInvalidCart = (cartLinesInvalid: IDictionary<boolean>): boolean => {

  for (const lineId of Object.keys(cartLinesInvalid)) {
      if (cartLinesInvalid[lineId]) {
          return true;
      }
  }

  return false;
};

//==================================================
//==================================================
const CartView: React.FC<ICartViewProps> = (props: ICartViewProps) => {
  const preventCheckout = _isInvalidCart(props.cartLineStatus);

  return (
  <div
    className={props.className}
    id={props.id}
    {...props.renderModuleAttributes(props)}
  >
    {props.title}
    <Node {...props.CartlinesWrapper}>
      {_renderCartlines(
        props.cartlines,
        props.resources.emptyCartText,
        props.CartlinesWrapper,
        props.storeSelector,
        props.backToShoppingButton,
        props.waitingComponent,
        props.cartLoadingStatus,
        props.cartDataResult,
        props.resources.itemsTitle,
        props.resources.qtyTitle,
        props.resources.unitPriceTitle,
        props.resources.totalTitle
      )}
    </Node>
    {props.orderSummaryHeading && (
      <Node {...props.OrderSummaryWrapper}>
        {props.orderSummaryHeading}
        {_renderOrderSummarylines(
          props.orderSummaryLineitems,
          props.OrderSummaryItems,
          props
        )}
        <Node className='checkout-btn-wrap'>
          {props.backToShoppingButton}
          {preventCheckout ? props.checkoutButtonDisabled : props.checkoutAsSignInUserButton}
        </Node>
        <div className='msc-promo-code'>
          {props.promoCode}
        </div>
      </Node>
    )}
    {props.storeSelector}
  </div>
);};

export default CartView;